import React from 'react';
import SectionHeading from './shared/SectionHeading';
import PrimaryButton from './shared/PrimaryButton';
import SecondaryButton from './shared/SecondaryButton';
import ServiceDetailCard from './shared/ServiceDetailCard';
import ConversionCTA from './home/ConversionCTA';
import FinalCTA from './home/FinalCTA';
import heroImage from '../assets/services-hero-optimized.jpg';
import supportImage from '../assets/filter-optimized.jpg';
import {
  PHONE_NUMBER,
  PHONE_NUMBER_LINK,
  PRIMARY_CTA_TEXT,
  SECONDARY_CTA_TEXT,
  SUPPORT_CTA_TEXT,
} from '../data/contact';
import { GOOGLE_REVIEWS_URL } from '../data/companyProfiles';
import ServiceCard from './shared/ServiceCard';
import services from '../data/services';

const serviceCategories = [
  {
    title: 'Installation Services',
    description:
      'New system planning and installation built around the home, comfort goals, and long-term efficiency.',
    bullets: [
      'New HVAC system installation for furnaces, air conditioners, heat pumps, and ductless systems',
      'Ductwork installation and modification',
      'Thermostat installation, including smart thermostat upgrades',
      'Zoning system installation',
      'Ventilation system installation including exhaust fans and air exchangers',
    ],
  },
  {
    title: 'Repair Services',
    description:
      'Straightforward diagnostics and repair work to restore comfort quickly without overcomplicating the fix.',
    bullets: [
      'Furnace and boiler repair',
      'AC repair and cooling diagnostics',
      'Heat pump service',
      'Ductwork and airflow troubleshooting',
      'Thermostat and sensor repair',
      'Refrigerant leak diagnosis',
    ],
  },
  {
    title: 'Maintenance',
    description:
      'Seasonal service designed to catch wear early, improve performance, and reduce surprise breakdowns.',
    bullets: [
      'Seasonal tune-ups for cooling and heating equipment',
      'Filter replacement guidance',
      'Cleaning coils, blower motors, and key components',
      'Checking refrigerant levels',
      'Inspecting electrical components and safety controls',
    ],
  },
  {
    title: 'Indoor Air Quality Services',
    description:
      'Indoor comfort solutions that support cleaner air, better circulation, and a healthier home environment.',
    bullets: [
      'Air purifiers and filtration systems',
      'Humidifiers and dehumidifiers',
      'UV light installation for air disinfection',
      'Carbon monoxide testing',
    ],
  },
  {
    title: 'Energy Efficiency and Upgrades',
    description:
      'Targeted system improvements that help homeowners reduce waste and make comfort more consistent.',
    bullets: [
      'Energy audits and performance inspections',
      'High-efficiency system upgrades',
      'Smart HVAC system integration',
    ],
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Reach out with the issue',
    description:
      'Call or request service online and share what the system is doing, what has changed, and how urgent comfort has become.',
  },
  {
    step: '02',
    title: 'Get practical recommendations',
    description:
      'After evaluating the system, we explain the problem, outline the options, and help you understand what makes sense next.',
  },
  {
    step: '03',
    title: 'Move forward with confidence',
    description:
      'Whether the right answer is repair, maintenance, or replacement planning, the goal is dependable comfort with no pressure.',
  },
];

const reassurancePoints = [
  {
    title: 'Emergency support available',
    description: '24-hour emergency service is available when the timing of a breakdown is not convenient.',
  },
  {
    title: 'Serving the area since 2003',
    description: 'Longstanding local service gives homeowners a more dependable point of contact over time.',
  },
  {
    title: 'Focused on residential comfort',
    description: 'Recommendations stay centered on comfort, airflow, budget, and what the home actually needs.',
  },
];

const Services = () => (
  <main>
    <section className="service-page-hero section">
      <div className="container service-page-hero-grid">
        <div className="service-page-hero-copy">
          <p className="section-eyebrow">Full Service Support</p>
          <h1>Heating, cooling, and indoor air solutions for Salt Lake County homes.</h1>
          <p className="service-page-hero-text">
            Preferred Service handles installation, repair, maintenance, indoor air quality, and
            system upgrades with a practical, homeowner-focused approach.
          </p>

          <div className="hero-actions">
            <PrimaryButton to="/contact">{PRIMARY_CTA_TEXT}</PrimaryButton>
            <SecondaryButton href={PHONE_NUMBER_LINK} aria-label={`Call Now at ${PHONE_NUMBER}`}>
              {SECONDARY_CTA_TEXT}
            </SecondaryButton>
          </div>

          <div className="service-page-hero-pills" aria-label="Preferred Service highlights">
            <span className="service-page-pill">Repairs, replacements, and tune-ups</span>
            <span className="service-page-pill">Indoor air quality and smart upgrades</span>
            <span className="service-page-pill">Serving South Jordan and surrounding areas</span>
          </div>
        </div>

        <div className="service-page-hero-media-wrap">
          <div className="service-page-hero-media">
            <img
              src={heroImage}
              alt="Residential air conditioning equipment outside a home"
              decoding="async"
              fetchPriority="high"
            />
          </div>
          <div className="card service-page-hero-panel">
            <p className="section-eyebrow">When To Call</p>
            <h2>Start with the comfort issue you are experiencing.</h2>
            <ul className="service-page-checklist">
              <li>Your heating or cooling system has stopped working</li>
              <li>Rooms are heating or cooling unevenly</li>
              <li>You need maintenance or replacement guidance</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="section section-surface">
      <div className="container">
        <SectionHeading
          eyebrow="Service Categories"
          title="Complete support for the systems that keep your home comfortable."
          description="Explore repair, installation, maintenance, indoor air quality, and efficiency services, then contact us if you are unsure which category fits the problem."
        />
        <div className="responsive-grid responsive-grid-3 service-detail-grid">
          {serviceCategories.map((service) => (
            <ServiceDetailCard key={service.title} {...service} />
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <SectionHeading
          eyebrow="Explore Core Services"
          title="Start with the service that best matches your comfort concern."
          description="Each guide explains common reasons to call and what to expect. If you are unsure where the problem fits, request service and tell us what you are noticing."
        />
        <div className="responsive-grid responsive-grid-3">
          {services.map((service) => (
            <ServiceCard key={service.slug} {...service} />
          ))}
        </div>
      </div>
    </section>

    <ConversionCTA
      eyebrow="Need Help Deciding?"
      title="If you are not sure whether this is a repair, tune-up, or replacement conversation, start with a service request."
      body="We can help narrow down the issue and point you toward the right next step without making the process feel complicated."
    />

    <section className="section section-dark">
      <div className="container">
        <SectionHeading
          eyebrow="What To Expect"
          title="A straightforward process from first contact to completed work."
          description="The goal is to make the next step obvious, explain the recommendation clearly, and keep the homeowner informed."
        />
        <div className="responsive-grid responsive-grid-3 process-grid">
          {processSteps.map((step) => (
            <article key={step.step} className="card process-card">
              <span className="process-step">{step.step}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="section trust-section">
      <div className="container service-reassurance-layout">
        <div className="service-reassurance-copy">
          <SectionHeading
            eyebrow="Reassurance"
            title="Trusted local service should still feel easy to move forward with."
            description="Clear explanations and practical recommendations help you make the right decision for your home without unnecessary pressure."
          />
          <div className="trust-social-proof" aria-label="Review platform ratings">
            <div className="trust-social-proof-item">
              <span className="trust-social-proof-name">Google Reviews</span>
              <span className="trust-social-proof-rating">5.0</span>
            </div>
            <div className="trust-social-proof-item">
              <span className="trust-social-proof-name">Emergency Service</span>
              <span className="trust-social-proof-rating">24 Hours</span>
            </div>
            <div className="trust-social-proof-item">
              <span className="trust-social-proof-name">Local Experience</span>
              <span className="trust-social-proof-rating">Since 2003</span>
            </div>
          </div>
          <div className="section-actions">
            <SecondaryButton href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
              {SUPPORT_CTA_TEXT}
            </SecondaryButton>
          </div>
        </div>

        <div className="service-reassurance-side">
          <div className="service-reassurance-image service-area-image">
            <img
              src={supportImage}
              alt="Clean air filter representing indoor comfort and maintenance"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="responsive-grid service-reassurance-grid">
            {reassurancePoints.map((point) => (
              <article key={point.title} className="card trust-card service-reassurance-card">
                <div className="trust-card-accent" aria-hidden="true" />
                <h3>{point.title}</h3>
                <p>{point.description}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>

    <FinalCTA />
  </main>
);

export default Services;
